import { useEffect, useState } from "react";
import { api } from "../api.js";

interface FreezeWindow {
  eventName: string;
  from: string;
  until: string;
}

interface DeployStatus {
  frozen: boolean;
  manual: boolean;
  window: FreezeWindow | null;
  next: FreezeWindow | null;
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString("da-DK", {
    timeZone: "Europe/Copenhagen",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Viser om serveren lige nu henter nye versioner automatisk, eller om
// deploy er frosset (omkring et event eller manuelt via mma-deploy freeze).
export function DeployStatusBadge() {
  const [status, setStatus] = useState<DeployStatus | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const s = await api.get<DeployStatus>("/api/deploy/status");
        if (!cancelled) {
          setStatus(s);
          setFailed(false);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    }
    load();
    const t = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (failed || !status) {
    return (
      <div className="text-xs text-neutral-500">
        ○ {failed ? "Deploy-status kunne ikke hentes" : "Henter deploy-status…"}
      </div>
    );
  }

  const lines: string[] = [];
  if (status.manual) lines.push("Frosset manuelt — slå til med mma-deploy unfreeze");
  if (status.window) {
    lines.push(`${status.window.eventName} · slås til igen ${fmt(status.window.until)}`);
  }
  if (!status.frozen) {
    lines.push(
      status.next
        ? `Fryses ${fmt(status.next.from)} → ${fmt(status.next.until)} (${status.next.eventName})`
        : "Ingen kommende events",
    );
  }

  return (
    <div
      className={`rounded-lg border px-3 py-2 text-xs ${
        status.frozen
          ? "border-amber-700 bg-amber-950/40"
          : "border-emerald-800 bg-emerald-950/30"
      }`}
      title="Mens deploy er frosset, lægges ny kode ikke på serveren — så en opdatering ikke kan genstarte systemet under en galla."
    >
      <div
        className={`font-semibold ${status.frozen ? "text-amber-400" : "text-emerald-400"}`}
      >
        ● {status.frozen ? "Deploy frosset" : "Auto-deploy aktiv"}
      </div>
      {lines.map((l) => (
        <div key={l} className="text-neutral-400">
          {l}
        </div>
      ))}
    </div>
  );
}
